import PropTypes from 'prop-types';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import Formsy from 'formsy-react';
import { Info } from 'lucide-react';
import { Button, Input, Label, FormGroup, DefaultTooltip, DefaultSheet, SheetBody, SheetFooter, TabsList, Tabs, TabsTrigger, TabsContent, Row, Col, Textarea } from 'twyne-new.ui';
import { isEmail } from 'validator';

import { useAppAlert } from '@Components/AppAlert';
import ConfirmationPopover from 'components/ConfirmationPopover'
import Switch from 'components/form/Switch';

const MAX_LINES = 25000

const propTypes = {
  onSuppresConsumer: PropTypes.func,
  onDeleteConsumer: PropTypes.func,
  disabled: PropTypes.bool,
  email: PropTypes.string,
};

function BulkAdd({ onChange, value, currentLines }) {
  const disabled = currentLines >= MAX_LINES
  return <FormGroup>
    <Label style={{ color: disabled ? 'red' : '' }}>Emails ({`${currentLines}/${MAX_LINES}`})
      <DefaultTooltip content='One email per line.'>
        <Link to='#' id='input-textarea-help'>
          <Info size={16} />
        </Link>
      </DefaultTooltip>
    </Label>
    <Textarea
      type="textarea"
      rows="10"
      onChange={onChange}
      value={value}
    />
  </FormGroup>
}

function SigleAdd({ value, onChange }) {
  return <FormGroup>
    <Label>Email</Label>
    <Input
      value={value}
      type="email"
      placeholder="Enter email"
      onChange={onChange}
    />
  </FormGroup>
}

function OptoutTabs(props) {
  return <Tabs defaultValue='suppress'>
    <TabsList>
      <TabsTrigger value='suppress'>Suppress</TabsTrigger>
      <TabsTrigger value='delete'>Delete</TabsTrigger>
    </TabsList>
    <TabsContent value='suppress'>
      <TabContent type="suppress" {...props} />
    </TabsContent>
    <TabsContent value='delete'>
      <TabContent type="delete" {...props} />
    </TabsContent>
  </Tabs>
}

function TabContent({ type, onSuppresConsumer, onDeleteConsumer, email, toggle }) {
  const { handleSuccess, handleError } = useAppAlert()
  const [bulk, setBulk] = useState(false)
  const [value, setValue] = useState(email)
  const [currentLines, setCurrentLines] = useState(1)

  const isValid = line => line && line.trim().length > 0

  const removeMaxLines = value => {
    const textSplited = value.split('\n')
    const textSliced = textSplited.slice(0, MAX_LINES)
    const textJoined = textSliced.join('\n')

    return textJoined
  }

  const filterLines = linesToFilter => {
    const linesFiltered = []
    linesToFilter.split("\n").forEach(line => {
      if (isValid(line) && isEmail(line) && !linesFiltered.includes(line)) {
        linesFiltered.push(line)
      }
    })
    setCurrentLines(linesFiltered.length)

    return linesFiltered
  }

  const handleTextAreaChange = async ({ target: { value } }) => {
    const filtered = filterLines(value)

    if (filtered.length >= MAX_LINES) {
      setCurrentLines(MAX_LINES)

      const valueMaxLine = removeMaxLines(value)
      setValue(valueMaxLine)
    } else {
      setValue(value)
    }
  }

  const feedback = onSubmit => async () => {
    try {
      toggle()
      const emailsParams = filterLines(value)
      await onSubmit(emailsParams)
      handleSuccess("Success", "Your request is being processed")
    } catch (e) {
      handleError("Error", "Your request was not processed. Try again.")
    }
  }

  const handleToggleBulk = active => {
    if (!active) {
      setValue(email)
      setCurrentLines(1)
    }
    setBulk(active)
  }

  const config = {
    suppress: {
      title: 'Add consumer in suppress list',
      btnConfirmId: 'addSuppresButton',
      btnConfirmLabel: 'Add to suppress list',
      btnConfirmTip: 'This action will suppress all the consumer personal data to the site and partners. This can not be undone.',
      onValid: onSuppresConsumer,
    },
    delete: {
      title: 'Delete consumer informations (CCPA)',
      btnConfirmId: 'deleteButton',
      btnConfirmLabel: 'Delete',
      btnConfirmTip: 'This action will delte all the consumer personal data permanently. This can not be undone.',
      onValid: onDeleteConsumer,
    },
  }

  return <Formsy className='flex flex-col gap-2'>
    <div className='flex items-center justify-between'>
      <span className='twy-text-base-accent'>{config[type].title}</span>
      <div className='flex items-center gap-2'>
        <Label>Bulk add</Label>
        <Switch name="bulk" value={bulk} setValue={handleToggleBulk} />
      </div>

    </div>
    <Row>
      <Col size="12">
        {bulk ? <BulkAdd onChange={handleTextAreaChange} value={value} currentLines={currentLines} /> : <SigleAdd onChange={({ target: { value } }) => setValue(value)} value={value} />}
      </Col>
    </Row>
    <Confirm id={config[type].btnConfirmId} label={config[type].btnConfirmLabel} validation={type}
      onValid={feedback(config[type].onValid)}
      tip={config[type].btnConfirmTip} />
  </Formsy>
}

export default function OptoutModal({ onSuppresConsumer, onDeleteConsumer, disabled, email }) {
  const [open, setOpen] = useState(false)
  const toggle = () => setOpen(open => !open)

  return <>
    <DefaultSheet
      title='Opt-out actions'
      trigger={<Button className='mt-5' variant='light' color="accent" disabled={disabled}>Opt out</Button>}
      wrapperProps={{ open, onOpenChange: setOpen }}
    >
      <SheetBody>
        <div className='text-md mb-2'>
          These actions handle all privacy features you need to take action.
        </div>
        <OptoutTabs onSuppresConsumer={onSuppresConsumer} onDeleteConsumer={onDeleteConsumer} email={email} toggle={toggle} />
      </SheetBody>
      <SheetFooter>
        <Button variant="ghost" onClick={toggle}>
          Cancel
        </Button>
      </SheetFooter>
    </DefaultSheet>
  </>
}
OptoutModal.propTypes = propTypes

function Confirm({ id, label, tip, validation, onValid }) {
  const [validationValue, setValidationValue] = useState('')
  const [isValid, setIsValid] = useState(false)

  const handleChangeValidation = evt => {
    const value = evt.target.value.toUpperCase()
    setIsValid(value === validation.toUpperCase())
    setValidationValue(value)
  }

  return <ConfirmationPopover id={id}
    target={<Button id={id} color="danger" className='mt-2 float-right'>{label}</Button>}
    title='Are you sure?'>
    <div className='flex flex-col gap-2'>
      <p>{tip}</p>
      <p className='twy-text-danger'>Type <i>{validation}</i> to confirm this action.</p>
      <Input value={validationValue} onChange={handleChangeValidation} />
      <Button onClick={onValid} color="danger" className='mt-1' block disabled={!isValid}>Confirm</Button>
    </div>
  </ConfirmationPopover>
}
